import React, { useState, useEffect } from "react";
import api from "../services/api";
import "../styles/Report.css";

function truncate(text) {
  if (!text) return "";
  return text.length > 50 ? text.substring(0, 50) + "..." : text;
}

function Report() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // ✅ Sorting State
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'ascending'
  });

  // ✅ Paging State
  const rowsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);

  // ✅ Details Modal State
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // ✅ Email Modal State
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    fromEmail: "My Inbox Media<info@mimpro.co>",
    toEmail: "",
    ccEmail: "",
    subject: "",
    body: "",
    format: "Text"
  });
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/";
  };

  const fetchReport = async () => {
    try {
      const res = await api.get("/api/Report/GetIncomingReport", {
        params: { fromDate, toDate }
      });
      setData(res.data);
      setFilteredData(res.data);
      setCurrentPage(1);
      setSortConfig({ key: null, direction: 'ascending' });
    } catch (err) {
      console.error("Error fetching report:", err);
      alert("Failed to load report");
    }
  };

  // ✅ Sorting Function
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });

    const sortedData = [...filteredData].sort((a, b) => {
      let aValue = a[key] || '';
      let bValue = b[key] || '';

      if (key === 'MailReceivedTime') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else {
        aValue = aValue.toString().toLowerCase();
        bValue = bValue.toString().toLowerCase();
      }

      if (aValue < bValue) {
        return direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });

    setFilteredData(sortedData);
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
    }
    return ' ⇅';
  };

  // ✅ Format date to show date and time separately
  const formatDateTime = (dateString) => {
    if (!dateString) return { date: "N/A", time: "" };
    try {
      const date = new Date(dateString);
      const formattedDate = date.toLocaleDateString();
      const formattedTime = date.toLocaleTimeString();
      return { date: formattedDate, time: formattedTime };
    } catch {
      return { date: dateString, time: "" };
    }
  };

  const openDetailsModal = (row) => {
    setSelectedRow(row);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedRow(null);
  };

  const openEmailModalFromDetails = () => {
    if (selectedRow) {
      setEmailData({
        fromEmail: "My Inbox Media<info@mimpro.co>",
        toEmail: selectedRow.MailFrom || "",
        ccEmail: selectedRow.MailCC || "",
        subject: `Re: ${selectedRow.MailSubject || ""}`,
        body: "",
        format: "Text"
      });
      setShowEmailModal(true);
      setShowDetailsModal(false);
    }
  };

  const closeEmailModal = () => {
    setShowEmailModal(false);
    setIsSending(false);
    setEmailData({
      fromEmail: "My Inbox Media<info@mimpro.co>",
      toEmail: "",
      ccEmail: "",
      subject: "",
      body: "",
      format: "Text"
    });
  };

  const clearTextArea = () => {
    setEmailData(prev => ({
      ...prev,
      body: ""
    }));
  };

  const handleFormatChange = (format) => {
    setEmailData(prev => ({
      ...prev,
      format: format
    }));
  };

  const handleBodyChange = (e) => {
    setEmailData(prev => ({
      ...prev,
      body: e.target.value
    }));
  };

  const sendEmail = async () => {
    try {
      if (!emailData.toEmail || !emailData.subject || !emailData.body) {
        alert("Please fill in all required fields (To, Subject, and Body)");
        return;
      }

      setIsSending(true);

      const payload = {
        fromEmail: emailData.fromEmail,
        toEmail: emailData.toEmail,
        ccEmail: emailData.ccEmail,
        subject: emailData.subject,
        body: emailData.body,
        format: emailData.format
      };

      const response = await api.post("/api/Report/ReplyIncomingEmail", payload);

      if (response.data) {
        const message = response.data.message || "Email sent successfully!";
        alert(message);
        closeEmailModal();
      } else {
        alert("Email sent successfully!");
        closeEmailModal();
      }
    } catch (err) {
      console.error("Error sending email:", err);
      const errorMessage = err.response?.data?.message ||
        err.response?.data ||
        "Failed to send email. Please try again.";
      alert(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  const isHTML = (str) => {
    if (!str) return false;

    const doc = new DOMParser().parseFromString(str, "text/html");
    return Array.from(doc.body.childNodes).some(node => node.nodeType === 1);
  };

  // ✅ SIMPLE HTML RENDERER - This will preserve all HTML formatting
  const renderHTML = (htmlContent) => {
    if (!htmlContent) return null;

    return (
      <div
        className="html-renderer"
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    );
  };

  // ✅ Paging Calculations
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  return (
    <div className="report-container">
      <div className="report-header">
        <div className="header-top">
          <h2>Incoming Email Report</h2>
          <button className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>

        <div className="filter-row">
          <label>From:</label>
          <input type="date" onChange={e => setFromDate(e.target.value)} />
          <label>To:</label>
          <input type="date" onChange={e => setToDate(e.target.value)} />
          <button onClick={fetchReport}>Search</button>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th onClick={() => requestSort('MailReceivedTime')} className="sortable-header">
                Received Time {getSortIndicator('MailReceivedTime')}
              </th>
              <th onClick={() => requestSort('MailFrom')} className="sortable-header">
                From {getSortIndicator('MailFrom')}
              </th>
              <th onClick={() => requestSort('MailCC')} className="sortable-header">
                CC {getSortIndicator('MailCC')}
              </th>
              <th onClick={() => requestSort('MailSubject')} className="sortable-header">
                Subject {getSortIndicator('MailSubject')}
              </th>
              <th>Body</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {currentRows.length > 0 ? (
              currentRows.map((row) => {
                const dateTime = formatDateTime(row.MailReceivedTime);
                return (
                  <tr key={row.Id}>
                    <td className="datetime-cell">
                      <div className="date">{dateTime.date}</div>
                      <div className="time">{dateTime.time}</div>
                    </td>
                    <td>{row.MailFrom}</td>
                    <td>{row.MailCC}</td>
                    <td title={row.MailSubject}>{truncate(row.MailSubject)}</td>
                    <td title={row.MailBody}>{truncate(row.MailBody)}</td>
                    <td>
                      <button
                        className="view-details-btn"
                        onClick={() => openDetailsModal(row)}
                      >
                        👁️ View Details
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6" style={{ textAlign: "center" }}>No data found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredData.length > rowsPerPage && (
        <div className="pagination">
          <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>◀ Prev</button>
          <span>Page {currentPage} of {totalPages}</span>
          <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>Next ▶</button>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedRow && (
        <div className="modal-overlay">
          <div className="modal-content details-modal">
            <div className="modal-header">
              <h3>Email Details</h3>
              <button className="close-btn" onClick={closeDetailsModal}>×</button>
            </div>

            <div className="modal-body">
              <div className="details-grid">
              
                <div className="detail-item">
                  <span className="detail-label">Received:</span>
                  <span className="detail-value datetime-detail">
                    <div className="detail-date">{formatDateTime(selectedRow.MailReceivedTime).date}</div>
                    <div className="detail-time">{formatDateTime(selectedRow.MailReceivedTime).time}</div>
                  </span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">From:</span>
                  <span className="detail-value">{selectedRow.MailFrom || 'N/A'}</span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">To:</span>
                  <span className="detail-value">{selectedRow.MailTo || 'N/A'}</span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">CC:</span>
                  <span className="detail-value">{selectedRow.MailCC || 'N/A'}</span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Subject:</span>
                  <span className="detail-value detail-subject">{selectedRow.MailSubject || 'N/A'}</span>
                </div>

                <div className="detail-item full-width">
                  <span className="detail-label">Body:</span>
                  <div className="detail-body-content">
                    {selectedRow.MailBody ? (
                      <div
                        className="html-renderer"
                        dangerouslySetInnerHTML={{
                          __html: isHTML(selectedRow.MailBody)
                            ? selectedRow.MailBody
                            : selectedRow.MailBody
                              .replace(/(Mobile\s\+\d+)/gi, "<br/><br/>$1")
                              .replace(/(website\s)/gi, "<br/><br/>$1")
                              .replace(/(Email\s)/gi, "<br/><br/>$1")
                              .replace(/(Location\s)/gi, "<br/><br/>$1")
                              .replace(/\n/g, "<br/>")
                        }}
                      />
                    ) : (
                      "N/A"
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="send-email-from-details-btn" onClick={openEmailModalFromDetails}>
                ✉️ Send Email Reply
              </button>
              <button className="close-btn" onClick={closeDetailsModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Send Email Reply</h3>
              <button className="close-btn" onClick={closeEmailModal}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>From:</label>
                <input type="text" value={emailData.fromEmail} disabled className="disabled-input" />
              </div>

              <div className="form-group">
                <label>To:</label>
                <input type="email" value={emailData.toEmail} onChange={(e) => setEmailData(prev => ({ ...prev, toEmail: e.target.value }))} placeholder="Enter recipient email" required disabled={isSending} />
              </div>

              <div className="form-group">
                <label>CC:</label>
                <input type="email" value={emailData.ccEmail} onChange={(e) => setEmailData(prev => ({ ...prev, ccEmail: e.target.value }))} placeholder="Enter CC email" disabled={isSending} />
              </div>

              <div className="form-group">
                <label>Subject:</label>
                <input type="text" value={emailData.subject} onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))} placeholder="Enter subject" required disabled={isSending} />
              </div>

              <div className="form-group">
                <label>Format:</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input type="radio" name="format" value="Text" checked={emailData.format === "Text"} onChange={() => handleFormatChange("Text")} disabled={isSending} /> Text
                  </label>
                  <label className="radio-label">
                    <input type="radio" name="format" value="HTML" checked={emailData.format === "HTML"} onChange={() => handleFormatChange("HTML")} disabled={isSending} /> HTML
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Body:</label>
                <textarea value={emailData.body} onChange={handleBodyChange} placeholder="Enter email body" rows="6" required disabled={isSending} />
              </div>

              {isSending && <div className="sending-indicator">Sending email...</div>}
            </div>

            <div className="modal-footer">
              <button className="clear-btn" onClick={clearTextArea} disabled={isSending}>Clear</button>
              <button className="send-btn" onClick={sendEmail} disabled={isSending}>{isSending ? 'Sending...' : 'Send Email'}</button>
              <button className="close-btn" onClick={closeEmailModal} disabled={isSending}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Report;